import { useEffect, useMemo, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Heart, Pencil, Copy, Search } from 'lucide-react'
import { apiFetch } from '@/lib/apiFetch'
import { cn } from '@/lib/utils'
import { useAuth } from '@/context/AuthContext'
import { VOICE_SLOTS } from '@/lib/voice'
import AbcNotation from '@/components/AbcNotation'
import TagChip from '@/components/TagChip'
import VoiceBadge from '@/components/VoiceBadge'
import type { Tag, VoiceSlot, Warmup } from '@/lib/types'

export default function WarmupsPage() {
  const { user } = useAuth()
  const history = useHistory()
  const [warmups, setWarmups] = useState<Warmup[] | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<number | null>(null)

  // Filters: name (substring), voices (OR), tags (AND).
  const [nameQuery, setNameQuery] = useState('')
  const [voices, setVoices] = useState<Set<VoiceSlot>>(new Set())
  const [tags, setTags] = useState<Set<string>>(new Set())

  useEffect(() => {
    apiFetch<Warmup[]>('/warmups')
      .then(setWarmups)
      .catch((e) => setError(e.message))
  }, [])

  // The browse page shows active exercises only (retired ones live in the Studio).
  const library = useMemo(() => (warmups ?? []).filter((w) => w.isActive), [warmups])

  // Tag chips are the tags actually present in the library.
  const allTags = useMemo(() => {
    const map = new Map<string, Tag>()
    library.forEach((w) => w.tags.forEach((t) => map.set(t.slug, t)))
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [library])

  const toggleVoice = (slug: VoiceSlot) =>
    setVoices((prev) => {
      const next = new Set(prev)
      next.has(slug) ? next.delete(slug) : next.add(slug)
      return next
    })

  const toggleTag = (slug: string) =>
    setTags((prev) => {
      const next = new Set(prev)
      next.has(slug) ? next.delete(slug) : next.add(slug)
      return next
    })

  const clearFilters = () => {
    setNameQuery('')
    setVoices(new Set())
    setTags(new Set())
  }

  const filtered = useMemo(() => {
    const q = nameQuery.trim().toLowerCase()
    return library.filter((w) => {
      if (q && !w.name.toLowerCase().includes(q)) return false
      // Voices: OR — keep if it matches any selected voice (or none selected).
      if (voices.size > 0 && !voices.has(w.voice)) return false
      // Tags: OR — keep if it carries at least one selected tag (or none selected).
      // The voice box and tag box are ANDed with each other.
      if (tags.size > 0) {
        const slugs = new Set(w.tags.map((t) => t.slug))
        let anyTag = false
        for (const t of tags) if (slugs.has(t)) { anyTag = true; break }
        if (!anyTag) return false
      }
      return true
    })
  }, [library, nameQuery, voices, tags])

  const hasFilters = nameQuery.trim() !== '' || voices.size > 0 || tags.size > 0

  const patch = (id: number, changes: Partial<Warmup>) =>
    setWarmups((prev) => prev?.map((w) => (w.id === id ? { ...w, ...changes } : w)) ?? prev)

  const toggleFavorite = async (w: Warmup) => {
    if (!user) {
      history.push('/login')
      return
    }
    const nextFav = !w.favoritedByMe
    // Optimistic: flip the heart and nudge the count immediately.
    patch(w.id, {
      favoritedByMe: nextFav,
      favoriteCount: w.favoriteCount + (nextFav ? 1 : -1),
    })
    try {
      const { favoriteCount } = await apiFetch<{ favoriteCount: number }>(
        `/warmups/${w.id}/favorite`,
        { method: nextFav ? 'PUT' : 'DELETE' }
      )
      patch(w.id, { favoriteCount })
    } catch (e: any) {
      // Roll back on failure.
      patch(w.id, { favoritedByMe: w.favoritedByMe, favoriteCount: w.favoriteCount })
      setError(e.message)
    }
  }

  const clone = async (w: Warmup) => {
    setBusy(w.id)
    setError('')
    try {
      const created = await apiFetch<Warmup>(`/warmups/${w.id}/clone`, { method: 'POST' })
      history.push(`/studio/warmups/${created.id}`)
    } catch (e: any) {
      setError(e.message)
      setBusy(null)
    }
  }

  const canEdit = (w: Warmup) => !!user && (user.isAdmin || w.createdByUserId === user.id)

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="spec-label mb-2">The library</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Exercises</h1>
          <p className="mt-2 text-muted-foreground">
            The full exercise library, each one notated, playable, and taken extremely seriously.
          </p>
        </div>
        {user && (
          <Link
            to="/studio/warmups/new"
            className="inline-flex min-h-[44px] items-center rounded-full bg-primary px-5 font-medium text-primary-foreground"
          >
            New exercise
          </Link>
        )}
      </div>

      {error && <p className="mt-6 text-destructive">{error}</p>}
      {!warmups && !error && <p className="mt-8 spec-label">Loading…</p>}

      {warmups && (
        <div className="mt-6 space-y-4 rounded-lg border bg-card p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
              placeholder="Filter by name…"
              className="min-h-[44px] w-full rounded-md border bg-background pl-9 pr-3 py-2"
            />
          </div>

          <div>
            <p className="spec-label mb-2">Voice · any of</p>
            <div className="flex flex-wrap gap-2">
              {VOICE_SLOTS.map(({ slug, label }) => (
                <button
                  key={slug}
                  type="button"
                  onClick={() => toggleVoice(slug)}
                  aria-pressed={voices.has(slug)}
                  className={cn(
                    'min-h-[44px] rounded-full border px-4 text-sm font-medium transition-colors',
                    voices.has(slug)
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background hover:border-primary/50 hover:bg-secondary'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {allTags.length > 0 && (
            <div>
              <p className="spec-label mb-2">Tags · any of</p>
              <div className="flex flex-wrap gap-2">
                {allTags.map((t) => (
                  <TagChip key={t.slug} tag={t} selected={tags.has(t.slug)} onClick={() => toggleTag(t.slug)} size="sm" />
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <p className="spec-label">
              {filtered.length} of {library.length}
            </p>
            {hasFilters && (
              <button type="button" onClick={clearFilters} className="spec-label underline-offset-2 hover:underline">
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {warmups && filtered.length === 0 && (
        <p className="mt-8 text-center text-muted-foreground">No exercises match those filters.</p>
      )}

      <div className="mt-8 space-y-4">
        {filtered.map((w) => (
          <div key={w.id} className="rounded-lg border bg-card p-4 transition-colors hover:border-primary/50">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/warmups/${w.slug}`} className="font-display text-xl font-semibold hover:underline">
                    {w.name}
                  </Link>
                  <VoiceBadge voice={w.voice} />
                </div>
                {w.description && <p className="mt-1 text-sm text-muted-foreground">{w.description}</p>}
              </div>

              <button
                type="button"
                onClick={() => toggleFavorite(w)}
                className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm transition-colors hover:bg-secondary"
                aria-pressed={w.favoritedByMe}
                aria-label={w.favoritedByMe ? 'Remove favorite' : 'Add favorite'}
              >
                <Heart
                  className={cn('h-4 w-4', w.favoritedByMe ? 'fill-coil text-coil' : 'text-muted-foreground')}
                />
                <span className="font-mono tabular-nums">{w.favoriteCount}</span>
              </button>
            </div>

            <Link to={`/warmups/${w.slug}`} className="mt-3 block">
              <div className="pointer-events-none">
                <AbcNotation abc={w.abcSource} />
              </div>
            </Link>

            <div className="mt-2 flex flex-wrap gap-1.5">
              {w.tags.map((t) => (
                <TagChip key={t.slug} tag={t} size="sm" />
              ))}
            </div>

            {user && (
              <div className="mt-3 flex flex-wrap gap-2 border-t pt-3">
                {canEdit(w) && (
                  <Link
                    to={`/studio/warmups/${w.id}`}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-4 text-sm font-medium hover:bg-secondary"
                  >
                    <Pencil className="h-4 w-4" /> Edit
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => clone(w)}
                  disabled={busy === w.id}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-4 text-sm font-medium hover:bg-secondary disabled:opacity-60"
                >
                  <Copy className="h-4 w-4" /> {busy === w.id ? 'Cloning…' : 'Clone'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
